/**
 * StemSeparator — share the vocal-remover ONNX engine with other tools.
 *
 * Public surface:
 *   await window.StemSeparator.split(audioBuffer, {
 *       onProgress: (pct, stage) => {},   // 0..1, optional
 *       slug: 'pitch',                    // tool slug for telemetry
 *       modelUrls: [...],                 // optional override
 *   })
 *   // resolves to { vocal: AudioBuffer, instrumental: AudioBuffer, mono: bool }
 *
 * All processing is in-browser. The ONNX model (~30 MB fp16) is fetched
 * lazily on the first call from the heavy() asset routing chain, then
 * cached by the runtime. Subsequent calls reuse the warm session.
 *
 * Why a shared module: vocal-remover.blade.php inlined this whole engine
 * (~250 lines including STFT/ISTFT/FFT). Pitch needs the same separator
 * for its "apply pitch only to vocals / instrumental" workflow. Rather
 * than copy-paste the inference loop, this module owns it — vocal-remover
 * will eventually migrate to use it too. For now it lives in parallel:
 * vocal-remover untouched, pitch consumes via this module. Same model,
 * same WASM artefact, same warm-up cost shared across the user's session.
 *
 * Architecture mirrors the EngineWarmer pattern (denoise / vocal-remover):
 * ORT init + model fetch happen once, behind a promise gate. Concurrent
 * callers serialize on the same gate; failed loads clear the gate so a
 * retry can recover. Progress is forwarded per-callback.
 */
(function(){
'use strict';

// ── Asset routing — RU-aware, mirrors vocal-remover blade. The heavy()
// resolution lives server-side (Blade renders <meta>); we read it here
// to avoid hard-coding paths. Falls back to the static path on /vendor/.
function defaultModelUrls(){
    var urls = [];
    var pushIfNew = function(u){ if (u && urls.indexOf(u) === -1) urls.push(u); };
    // Tool-level overrides (vr-model-url for /vocal-remover, /pitch, etc;
    // aid-stem-model-url for /ai-detector). Tools that pin a specific
    // ONNX variant set their own meta tags.
    var meta = document.querySelector('meta[name="vr-model-url"]')
            || document.querySelector('meta[name="aid-stem-model-url"]');
    var metaFb = document.querySelector('meta[name="vr-model-url-fallback"]')
              || document.querySelector('meta[name="aid-stem-model-url-fallback"]');
    if (meta && meta.content) pushIfNew(meta.content);
    if (metaFb && metaFb.content) pushIfNew(metaFb.content);
    // fp32 hardcoded fallback. Root-cause fix for WebGPU fp16 kernel
    // saturation on the UVR model — fp32 weights avoid the saturation
    // regime entirely. See config/assets.php for the cast recipe.
    pushIfNew('/vendor/onnx/vocal-remover-fp32.onnx');
    pushIfNew('https://cdn.timbrica.com/vendor/onnx/vocal-remover-fp32.onnx');
    return urls;
}

// ORT version pinned to match what /vocal-remover used pre-refactor.
// Bumping requires re-testing the masking inference output against the
// known-good fp16 model; some onnxruntime-web releases have shifted
// op-set defaults that cause subtle bit-different outputs even on the
// same model. Keep in lockstep with vocal-remover.blade.php's expected
// version unless both are revalidated together.
//
// ⚠️ WHICH build is not decided here any more: window.__ortBuild
// (device-capabilities.js) owns it. An iPhone gets the plain WASM build — with
// the JSEP one (ort.min.js) and WebGPU this engine finished for 0 of 16
// sessions of /remove-word-from-song on Safari 26 in 60 days: the tab died right
// after `model_loaded` (#7527 «Страница вылетает», 22.09.2026). A Mac or iPad
// keeps JSEP + WebGPU, measured working there (ORT_OPTS below). The literal is
// only for a page without the owner (OrtBuildSingleOwnerTest).
// Both separators ask for the SAME answer (they share one window.ort): keep the
// JSEP build + WebGPU on desktop-class WebKit, where production measured it
// working and 3–20× faster than wasm (Mac/iPad Safari 26/27); iPhone gets the
// plain wasm build (see __ortBuild.jsepOnDesktopWebKit for the numbers).
var ORT_OPTS = { jsepOnDesktopWebKit: true };
function ortBuild(){ return window.__ortBuild || null; }
// ORT 1.18 — the lane of a browser that cannot RESERVE the shared 4 GiB memory the
// current build imports (WebKit before 18: «no available backend found. ERR: [wasm]
// RangeError: Out of memory» at the first session). The owner learns it from the
// first failure (noShareStepDown below tells it) and answers the legacy build from
// then on. Only with SIMD: a CPU without it keeps its own answer in this file.
function ortLegacy(){
    var b = ortBuild(), L = (b && typeof b.legacy === 'function') ? b.legacy() : null;
    return (L && L.simd) ? L : null;
}
function ortPrimary(){ var L = ortLegacy(); if (L) return L.url; var b = ortBuild(); return b ? b.url(ORT_OPTS) : '/vendor/onnxruntime/ort.min.js'; }
function ortFallback(){ var L = ortLegacy(); if (L) return L.fallbackUrl; var b = ortBuild(); return (b && typeof b.fallback === 'function') ? b.fallback(ORT_OPTS) : null; }
function ortWasmBase(){ var L = ortLegacy(); if (L) return L.wasmBase; var b = ortBuild(); return (b && typeof b.wasmBase === 'function') ? b.wasmBase() : '/vendor/onnxruntime/'; }
function ortWasmOnly(){ if (ortLegacy()) return true; var b = ortBuild(); return !!(b && typeof b.wasmOnly === 'function' && b.wasmOnly(ORT_OPTS)); }
// Was that failure the reservation one, and is there a build that needs none? Then
// the runtime that failed is dropped (a failed init is cached by it; the generation
// bump tells the other separator, which shares window.ort), and 1.18 is loaded in its
// place — after a pause: an immediate retry meets the same address space.
// Resolves true when the caller should create its session once more, on ['wasm'].
// «Is the runtime on the page the legacy one» is read off the runtime itself:
// window.ort is shared, and the other separator may be the one that loaded it.
function ortOnLegacy(){
    try { var v = window.ort && ort.env && ort.env.versions && (ort.env.versions.web || ort.env.versions.common); return /^1\.18\./.test(String(v || '')); }
    catch(_){ return false; }
}
async function noShareStepDown(slug, err, from){
    var b = ortBuild();
    if (!(b && typeof b.initFailed === 'function')) return false;
    if (!b.initFailed(err, { legacy: ortOnLegacy() ? 'legacy' : '', from: from })) return false;
    window.__ortGen = ortGen() + 1;
    try { delete window.ort; } catch(_){ try { window.ort = undefined; } catch(__){} }
    ortLoadPromise = null;
    await new Promise(function(r){ setTimeout(r, 1000); });
    try { await loadOrtScript(); } catch(_){ return false; }
    reportTelemetry(slug, 'ort_noshare_stepdown', { from: from, legacy: ortOnLegacy() ? 1 : 0 });
    return ortOnLegacy();
}

// ── Engine state (shared across all callers) ──
var ortLoadPromise = null;
var ortLoadGen = -1;   // window.__ortGen the load promise belongs to
var modelLoadPromise = null;
var onnxSession = null;
var sessionProvider = null;   // the EP the live session was created for
// WebGPU wedged on this page (a chunk never came back): the rest of the page
// life runs on wasm. Android, 60 d to 24.09.2026: 35 first-chunk timeouts in
// 307 WebGPU sessions — each one used to end the separation with an error.
var webgpuWedged = false;
var modelLoadedSize = 0;
var sessionGen = -1; // window.__ortGen this session was created under

// Shared ORT-runtime generation — kept in lockstep with mdx-stem-separator.js
// (see the big comment there): a WebAssembly TRAP in ANY consumer corrupts the
// one shared wasm instance, so the trapping module bumps window.__ortGen and
// drops window.ort; everyone else lazily discards sessions from the dead
// generation and re-inits fresh. Duplicated 10-line primitive by design —
// both files are raw-loadable and must not depend on load order.
function ortGen(){ return window.__ortGen | 0; }
function isWasmTrap(e){
    if (!e) return false;
    if (typeof WebAssembly !== 'undefined' && WebAssembly.RuntimeError && e instanceof WebAssembly.RuntimeError) return true;
    var m = String((e && e.message) || e);
    return /memory access out of bounds|unreachable(?: executed)?|table index is out of bounds|index out of bounds|call stack exhausted/i.test(m);
}
function ortTrapReset(slug, where){
    window.__ortGen = ortGen() + 1;
    try { delete window.ort; } catch(_){ try { window.ort = undefined; } catch(__){} }
    ortLoadPromise = null;
    reportTelemetry(slug, 'ort_runtime_trap_reset', { where: where, gen: ortGen() });
}

function reportTelemetry(slug, action, payload){
    try {
        if (typeof window.__ae === 'function') {
            window.__ae('feature_use', Object.assign({ slug: slug || 'stem-separator', action: action }, payload || {}));
        }
    } catch(_){}
}

function loadOrtScript(){
    if (window.ort) return Promise.resolve('cached');
    // A promise from a DEAD generation is not a runtime: when another module
    // drops window.ort (ortTrapReset, or a page handing the runtime to another
    // engine), this module's promise is still the resolved one from before —
    // returning it handed callers `ort === undefined` and «Cannot read
    // properties of undefined (reading 'InferenceSession')» (reproduced
    // 24.09.2026: Kim on /profanity-remover, then CascadedNet). The generation
    // decides whether the promise still means anything.
    if (ortLoadPromise && ortLoadGen !== ortGen()) ortLoadPromise = null;
    if (ortLoadPromise) return ortLoadPromise;
    ortLoadGen = ortGen();
    ortLoadPromise = new Promise(function(resolve, reject){
        function tryLoad(url, onFail){
            var s = document.createElement('script');
            // Cache-bust URL on retry so a corrupted previous load doesn't
            // hand us the same broken module from disk cache (50/56 Deep
            // failures post-fp32 deploy were "previous call to initWasm()
            // failed" — once ORT poisons its internal state the only
            // recovery is a fresh module load).
            s.src = url + (url.indexOf('?') === -1 ? '?' : '&') + 'ort_reload=' + Date.now();
            s.onload = function(){
                try {
                    if (window.ort && ort.env && ort.env.wasm) {
                        // 1.18 runs its proxy worker from a blob: URL, where a
                        // root-relative path does not resolve («Failed to parse URL»)
                        var _wp = ortWasmBase();
                        if (onLegacy && _wp.charAt(0) === '/' && typeof location !== 'undefined' && location.origin) _wp = location.origin + _wp;
                        ort.env.wasm.wasmPaths = _wp;
                        // the legacy dir has no threaded wasm
                        ort.env.wasm.numThreads = (!onLegacy && typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated)
                            ? Math.min(navigator.hardwareConcurrency || 4, 4)
                            : 1;
                    }
                } catch(_){}
                resolve('loaded');
            };
            s.onerror = onFail;
            document.head.appendChild(s);
        }
        var fb = ortFallback(), onLegacy = !!ortLegacy();
        tryLoad(ortPrimary(), function(){
            if (!fb) { ortLoadPromise = null; reject(new Error('ORT load failed (primary; no fallback owner)')); return; }
            tryLoad(fb, function(){
                ortLoadPromise = null;
                reject(new Error('ORT load failed (both sources)'));
            });
        });
    });
    return ortLoadPromise;
}

// ORT-Web poison-state recovery: when a previous InferenceSession.create
// fails inside initWasm() (network flake, WASM fetch 404, SAB unavailable
// across tabs, etc.), ORT-Web caches that failure on the window.ort
// global. Every subsequent create() call throws "no available backend
// found. ERR: [wasm] Error: previous call to 'initWasm()' failed" without
// actually retrying init. Telemetry 2026-05-28..06-13 shows this is 50/56
// (89 %) of all Deep failures post-fp32 deploy.
//
// Recovery: tear down the poisoned global, drop our cached load promise,
// reload the ORT script with a fresh URL (the cache-buster above), and
// re-init. Costs one extra ~300 KB script fetch — cheaper than failing.
async function resetOrtAfterPoison(slug){
    try {
        reportTelemetry(slug, 'ort_poison_reset_started', {});
        // Hard-reset every cached ORT handle we hold.
        try { if (window.ort && window.ort._unwarmedReset) window.ort._unwarmedReset(); } catch(_){}
        try { delete window.ort; } catch(_){ try { window.ort = undefined; } catch(__){ } }
        ortLoadPromise = null;
        onnxSession = null;
        modelLoadPromise = null;
        await loadOrtScript();
        reportTelemetry(slug, 'ort_poison_reset_done', {});
        return true;
    } catch (e) {
        reportTelemetry(slug, 'ort_poison_reset_failed', {
            reason: String((e && e.message) || e).slice(0, 160)
        });
        return false;
    }
}

function isOrtPoisonError(err){
    var m = err && err.message ? err.message : String(err);
    return /previous call to ['"]initWasm['"]\(\)/.test(m) ||
           /previous call to ['"]initOrt['"]\(\)/.test(m) ||
           (/no available backend/.test(m) && /previous call/.test(m));
}

async function fetchWithRetry(url, attempts, backoffMs, signal, headers){
    var lastErr = null;
    // Only thread a REAL AbortSignal into fetch — callers sometimes pass a
    // POJO {get aborted(){...}}, which fetch() rejects. .aborted is still
    // polled manually at every loop point.
    var fetchSignal = (typeof AbortSignal !== 'undefined' && signal instanceof AbortSignal) ? signal : undefined;
    for (var a = 0; a < attempts; a++) {
        if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        try {
            var r = await fetch(url, { credentials: 'omit', cache: a === 0 ? 'default' : 'reload', signal: fetchSignal, headers: headers || {} });
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r;
        } catch (e) {
            if (e && (e.name === 'AbortError' || (signal && signal.aborted))) throw e;
            lastErr = e;
            if (a < attempts - 1) {
                await new Promise(function(res){ setTimeout(res, backoffMs * Math.pow(2, a)); });
            }
        }
    }
    throw lastErr;
}

// Streaming download with mid-body Range resume — same fix as
// mdx-stem-separator.js. fetchWithRetry only re-tries the request START;
// a mid-body network blip on the ~30 MB transfer used to throw the whole
// download away ("Failed to fetch" class, 27 events/14d per the old comment).
// The edge serves Range (206), so resume from the received offset instead.
var MAX_RESUMES = 4;
async function downloadWithResume(url, slug, onProgress, signal){
    var chunks = [], loaded = 0, expectedTotal = 0, resumes = 0;
    while (true) {
        if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        var headers = loaded > 0 ? { 'Range': 'bytes=' + loaded + '-' } : null;
        var r = await fetchWithRetry(url, 3, 500, signal, headers);
        if (loaded > 0 && r.status !== 206) {
            // Server or SW ignored Range and sent the full body — restart assembly.
            chunks = []; loaded = 0;
        }
        if (!expectedTotal) {
            if (r.status === 206) {
                var cr = r.headers.get('Content-Range');
                var m = cr && cr.match(/\/(\d+)\s*$/);
                if (m) expectedTotal = parseInt(m[1], 10);
            } else {
                expectedTotal = parseInt(r.headers.get('Content-Length'), 10) || 0;
            }
        }
        var loadedAtAttemptStart = loaded;
        try {
            var reader = r.body.getReader();
            while (true) {
                if (signal && signal.aborted) {
                    try { reader.cancel(); } catch(_){}
                    throw new DOMException('Aborted', 'AbortError');
                }
                var read = await reader.read();
                if (read.done) break;
                chunks.push(read.value);
                loaded += read.value.length;
                if (onProgress && expectedTotal) onProgress(Math.min(1, loaded / expectedTotal));
            }
            // A cleanly-ended but short stream is still an interrupted download.
            if (expectedTotal && loaded < expectedTotal) throw new Error('short body ' + loaded + '/' + expectedTotal);
            break; // complete
        } catch (e) {
            if (e && (e.name === 'AbortError' || (signal && signal.aborted))) throw e;
            /* Лимит обязан означать «подряд без единого байта», а не «всего
               обрывов за загрузку». Иначе на слабом канале четыре обрыва
               убивают загрузку, даже если каждая попытка приносила мегабайты,
               — а это ровно тот дефект, из-за которого /music-genre-detector
               терял 14 сессий за 30 дней ПРИ работающей докачке. */
            if (loaded - loadedAtAttemptStart >= 256 * 1024) resumes = 0;   // сброс только за СОДЕРЖАТЕЛЬНЫЙ прогресс
            resumes++;
            if (resumes > MAX_RESUMES) throw e;
            await new Promise(function(res){ setTimeout(res, 400 * resumes); });
        }
    }
    if (resumes > 0) reportTelemetry(slug, 'model_dl_resumed', { resumes: resumes, sizeMB: Math.round(loaded / 1048576) });
    var bytes = new Uint8Array(loaded);
    var off = 0;
    for (var c = 0; c < chunks.length; c++) { bytes.set(chunks[c], off); off += chunks[c].length; }
    return bytes;
}

async function fetchModelBytes(slug, onProgress, signal){
    var urls = defaultModelUrls();
    var lastErr = null;
    for (var i = 0; i < urls.length; i++) {
        if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        try {
            var bytes = await downloadWithResume(urls[i], slug, onProgress, signal);
            modelLoadedSize = bytes.length;
            if (i > 0) reportTelemetry(slug, 'model_fallback', { tried: i, sizeMB: Math.round(bytes.length / 1048576) });
            return bytes;
        } catch (e) {
            if (e && (e.name === 'AbortError' || (signal && signal.aborted))) throw e;
            lastErr = e;
        }
    }
    throw new Error('Stem-separator model unavailable: ' + (lastErr && lastErr.message || 'all URLs failed'));
}

async function ensureModelLoaded(slug, onProgress, signal){
    // Session from a runtime generation that has since trapped points into
    // freed wasm memory — rebuild on the fresh runtime instead.
    if (onnxSession && sessionGen !== ortGen()) {
        try { onnxSession.release(); } catch(_){}
        onnxSession = null; modelLoadPromise = null;
    }
    if (onnxSession) return onnxSession;
    if (modelLoadPromise) return modelLoadPromise;
    modelLoadPromise = (async function(){
        await loadOrtScript();
        var bytes = await fetchModelBytes(slug, onProgress, signal);
        // The wasm-only build has no WebGPU EP (see ortWasmOnly above): asking
        // for one there is a failed provider init and nothing else.
        var hasWebGPU = false;
        if (!ortWasmOnly() && !webgpuWedged && 'gpu' in navigator) {
            try { hasWebGPU = !!(await navigator.gpu.requestAdapter()); } catch(_){}
        }
        // WebGPU re-enabled now that we ship a fp32 UVR. Provider list:
        // WebGPU first (5-10× faster stem split on big CPUs), WASM
        // fallback. Both safe with fp32 weights — the WebGPU saturation
        // regime that broke fp16 UVR for ~50 % of WebGPU users (feedback
        // #818 Гудман Сол hit 9 mask collapses in 6 minutes) lived in
        // ORT-Web's fp16 kernel implementations, not the model itself
        // (CPU fp16 vs fp32 verified identical to 0.003 on realistic
        // STFT magnitude inputs). With fp32 weights ORT-Web uses fp32
        // WebGPU kernels which have mature arithmetic — no saturation.
        // Defense-in-depth (stem_mask_collapsed observability) retained.
        var providers = hasWebGPU ? ['webgpu', 'wasm'] : ['wasm'];
        // Pure-wasm path → run the wasm backend in ORT's built-in proxy
        // worker. Measured (240 s stereo, 07-29): main-thread inference
        // saturates the page for ~7 minutes — rAF frozen for seconds at a
        // time, preview dead, "tab not responding" risk; that jank is the
        // residual abandonment driver after the ETA fix. proxy=true moves
        // session.run() compute off-main with ZERO custom worker code
        // (artefacts ship in /vendor/onnxruntime: ort-wasm-*.mjs). Scoped
        // to !hasWebGPU: the webgpu path's GPU compute is already async
        // (its failure mode is the hang, covered by the chunk watchdog),
        // and ort 1.24's proxy+jsep combination is not battle-tested here.
        // In-house precedent for worker-side ORT: supertonic-worker.js /
        // colorize-worker.js (dedicated workers); proxy is the same
        // isolation with ORT owning the plumbing.
        if (!hasWebGPU) {
            try { ort.env.wasm.proxy = true; } catch(_){}
        }
        try {
            // 'basic', NOT 'all' — the project etalon (apollo-worker.js:375,
            // ai-upscale-worker.js). Measured 2026-08-14 twice on this exact
            // model: session.create costs 2449 ms under 'all' against 348 ms
            // under 'basic' on WebGPU (7x), i.e. ~2 s of dead air before the
            // first separation, for optimisation passes this graph does not
            // benefit from. Same trap that cost DDColor-fp16 far more.
            onnxSession = await ort.InferenceSession.create(bytes.buffer, {
                executionProviders: providers,
                graphOptimizationLevel: 'basic'
            });
            reportTelemetry(slug, 'model_loaded', { sizeMB: Math.round(bytes.length / 1048576), provider: providers[0] });
            sessionProvider = providers[0];
            sessionGen = ortGen(); return onnxSession;
        } catch(e) {
            var emsg = (e && e.message || '') + '';

            // WebGPU-specific failure → retry with WASM-only providers.
            // ORT-Web's built-in ['webgpu', 'wasm'] internal fallback fails
            // to recover when the WebGPU jsep shim 404s from a stale CDN
            // (4 events / 30d) or the GPUDevice runs out of buffer
            // allocation room (1 event / 30d). The error bubbles out
            // labelled `[webgpu] ...` even though WASM was available.
            // Explicit retry with `['wasm']` forces the WASM kernel path
            // and recovers in those cases.
            var webgpuProblem = hasWebGPU && (
                emsg.indexOf('[webgpu]') >= 0 ||
                emsg.indexOf('createBuffer') >= 0 ||
                emsg.indexOf('GPUDevice') >= 0
            );
            if (webgpuProblem) {
                try {
                    reportTelemetry(slug, 'webgpu_fallback_to_wasm', { reason: emsg.substring(0, 120) });
                    onnxSession = await ort.InferenceSession.create(bytes.buffer, {
                        executionProviders: ['wasm'],
                        graphOptimizationLevel: 'basic'
                    });
                    reportTelemetry(slug, 'model_loaded', { sizeMB: Math.round(bytes.length / 1048576), provider: 'wasm-after-webgpu-fail' });
                    sessionGen = ortGen(); return onnxSession;
                } catch (eWasm) {
                    // WASM also failed — fall through to SIMD-fallback
                    // logic below in case the WASM failure is a SIMD
                    // capability issue.
                    e = eWasm;
                    emsg = (eWasm && eWasm.message || '') + '';
                }
            }

            // The browser could not RESERVE the current build's memory (WebKit
            // before 18): ORT 1.18 in a fresh runtime — see noShareStepDown. The
            // model bytes are still ours: the runtime died before it was handed
            // anything.
            if (bytes.byteLength && await noShareStepDown(slug, e, 'cascaded')) {
                try {
                    try { ort.env.wasm.proxy = true; } catch(_){}
                    onnxSession = await ort.InferenceSession.create(bytes.buffer, {
                        executionProviders: ['wasm'],
                        graphOptimizationLevel: 'basic'
                    });
                    reportTelemetry(slug, 'model_loaded', { sizeMB: Math.round(bytes.length / 1048576), provider: 'wasm-legacy' });
                    sessionProvider = 'wasm';
                    sessionGen = ortGen(); return onnxSession;
                } catch (eLegacy) {
                    e = eLegacy;
                    emsg = (eLegacy && eLegacy.message || '') + '';
                }
            }

            // SIMD fallback. Old Android Chrome / Yandex Browser ship without
            // WebAssembly SIMD; ORT 1.24.3 default builds require it and
            // surface "no available backend found. ERR: [wasm] Error:
            // WebAssembly SIMD is not supported". Telemetry showed 32 such
            // failures in 14d. Re-create the session with simd=false → loads
            // the non-SIMD WASM artefact (~2× slower but works on the long-
            // tail of mobile browsers).
            var simdProblem = emsg.indexOf('SIMD') >= 0 && (emsg.indexOf('not supported') >= 0 || emsg.indexOf('no available backend') >= 0);
            if (simdProblem && ort && ort.env && ort.env.wasm && ort.env.wasm.simd !== false) {
                try {
                    ort.env.wasm.simd = false;
                    ort.env.wasm.numThreads = 1; // non-SIMD path is single-threaded by spec
                    reportTelemetry(slug, 'wasm_simd_fallback', { reason: emsg.substring(0, 80) });
                    onnxSession = await ort.InferenceSession.create(bytes.buffer, {
                        executionProviders: ['wasm'],
                        graphOptimizationLevel: 'basic'
                    });
                    reportTelemetry(slug, 'model_loaded', { sizeMB: Math.round(bytes.length / 1048576), provider: 'wasm-nosimd' });
                    sessionGen = ortGen(); return onnxSession;
                } catch (e2) {
                    modelLoadPromise = null;
                    throw e2;
                }
            }

            // ORT-Web poisoned-state recovery — see resetOrtAfterPoison()
            // above for why. One-shot retry: tear down window.ort, reload
            // the script with a fresh URL, and re-create the session. If
            // that ALSO fails, surface the original error (cleaner stack).
            if (isOrtPoisonError(e)) {
                var resetOk = await resetOrtAfterPoison(slug);
                if (resetOk) {
                    try {
                        var providersR = hasWebGPU ? ['webgpu', 'wasm'] : ['wasm'];
                        onnxSession = await ort.InferenceSession.create(bytes.buffer, {
                            executionProviders: providersR,
                            graphOptimizationLevel: 'basic'
                        });
                        reportTelemetry(slug, 'model_loaded', { sizeMB: Math.round(bytes.length / 1048576), provider: providersR[0] + '-after-ort-reset' });
                        sessionGen = ortGen(); return onnxSession;
                    } catch (eRetry) {
                        // Retry failed — fall through to throw the original
                        // poison error (better signal than the retry error).
                    }
                }
            }

            modelLoadPromise = null;
            throw e;
        }
    })();
    return modelLoadPromise;
}

// ── DSP (Cooley-Tukey radix-2 FFT, Hann-windowed STFT/ISTFT) ──
//
// Identical to vocal-remover.blade.php's inline impl — copied bit-for-bit
// so output is interchangeable. When vocal-remover migrates to this
// module, drop the blade copy entirely.
//
// ⚠️ Повороты считаются РЕКУРРЕНТОЙ (cR*wR − cI*wI на каждой бабочке), а не по
// таблице. У соседнего движка замена этого на таблицу + упаковку двух каналов
// в одно преобразование дала ×2 (2026-08-22, см. `public/js/dsp-fft-mixed.js` —
// там же длины 2^a·{3,5,15}, которых радикс-2 не берёт). Здесь не сделано
// СОЗНАТЕЛЬНО: n_fft = 2048 при hop 1024, и по замеру телеметрии этот лейн —
// медиана 48 с против 151 у Ультры при 3.5 % завершений, то есть выигрыш есть,
// но окупает не он. Правишь этот файл по другому поводу — забирай оттуда.
function fftInPlace(re, im){
    var n = re.length;
    for (var i = 1, j = 0; i < n; i++) {
        var bit = n >> 1;
        for (; j & bit; bit >>= 1) j ^= bit;
        j ^= bit;
        if (i < j) { var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
    }
    for (var size = 2; size <= n; size <<= 1) {
        var half = size >> 1;
        var ang = 2 * Math.PI / size;
        var wR = Math.cos(ang), wI = Math.sin(ang);
        for (var i = 0; i < n; i += size) {
            var cR = 1, cI = 0;
            for (var j = 0; j < half; j++) {
                var uR = re[i+j], uI = im[i+j];
                var vR = re[i+j+half]*cR - im[i+j+half]*cI;
                var vI = re[i+j+half]*cI + im[i+j+half]*cR;
                re[i+j] = uR+vR; im[i+j] = uI+vI;
                re[i+j+half] = uR-vR; im[i+j+half] = uI-vI;
                var nR = cR*wR - cI*wI; cI = cR*wI + cI*wR; cR = nR;
            }
        }
    }
}
function ifftInPlace(re, im){
    var n = re.length;
    for (var i = 0; i < n; i++) im[i] = -im[i];
    fftInPlace(re, im);
    for (var i = 0; i < n; i++) { re[i] /= n; im[i] = -im[i] / n; }
}

function emptyBuffer(numberOfChannels, length, sampleRate){
    if (typeof window.__safeAudioBuffer === 'function') {
        return window.__safeAudioBuffer({ numberOfChannels: numberOfChannels, length: length, sampleRate: sampleRate });
    }
    if (typeof AudioBuffer === 'function') {
        try { return new AudioBuffer({ numberOfChannels: numberOfChannels, length: length, sampleRate: sampleRate }); } catch(_){}
    }
    var OC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    return new OC(numberOfChannels, length, sampleRate).createBuffer(numberOfChannels, length, sampleRate);
}

function yieldFrame(){ return new Promise(function(r){ setTimeout(r, 0); }); }

// Bounded await for onnxSession.run(). ORT-Web's webgpu backend can hang
// FOREVER on GPU device loss (Chrome revokes the device under memory
// pressure / driver reset; ort-web's run() promise never settles). Prod
// telemetry 07-03..07-29: 13 of 35 pitch stem_split_start had no split_ok
// and ZERO error events — the silent-hang class, incl. strong 16-32 GB
// desktops (bug-report ru/pitch 07-23 "Не скачивается понижение
// тональности": model_loaded{webgpu} then nothing). A timeout converts
// the infinite hang into a typed, user-visible failure. NOTE: rejection
// does not cancel the underlying GPU work (ORT has no abort) — callers
// must release the session so the next attempt re-inits fresh.
// The deadline counts VISIBLE time (window.__visibleTimeout, timbrica-core.js):
// a phone that switched apps or a hidden tab throttles the page, and on the
// wall clock a chunk that was merely paused read as a wedged backend — the
// whole separation was thrown away (60 d to 24.09.2026: ~35 such mid-song
// timeouts, almost all on phones, on average 30 % into the song).
function afterVisible(fn, ms){
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    var h = setTimeout(fn, ms);
    return function(){ clearTimeout(h); };
}
function visibleNow(){
    return (typeof window.__visibleNow === 'function') ? window.__visibleNow() : performance.now();
}
function runWithTimeout(promise, ms, chunkIdx){
    return new Promise(function(resolve, reject){
        var settled = false;
        var cancel = afterVisible(function(){
            if (settled) return;
            settled = true;
            var e = new Error('stem inference timed out (chunk ' + chunkIdx + ', ' + ms + 'ms)');
            e.code = 'STEM_INFER_TIMEOUT';
            e.expected = true;
            reject(e);
        }, ms);
        promise.then(function(v){ if (settled) return; settled = true; cancel(); resolve(v); },
                     function(err){ if (settled) return; settled = true; cancel(); reject(err); });
    });
}
// ── One ORT run at a time on the page ──────────────────────────────────────
// ORT-Web allows ONE session.run() in flight per runtime instance, and the
// failing second call clears the guard the first one holds — an overlap kills
// BOTH runs ("Session already started" + "Cannot read properties of null
// (reading 'Nd')"; see reference in 3d-printing-core.js). This engine and
// mdx-stem-separator.js share ONE window.ort, so the queue is per page, not per
// module. Measured on /remove-word-from-song (60 d to 23.09.2026): every such
// failure was a second file picked while the first was still separating.
// The timeout starts when the run STARTS — time spent waiting behind another
// engine's chunk is not a hang. Chained on both settle paths so one failed run
// cannot wedge the queue. Duplicated in mdx-stem-separator.js by design (both
// files are raw-loadable, neither may depend on the other's load order).
function ortRunSerialized(fn){
    var gate = window.__ortRunGate || (window.__ortRunGate = { tail: Promise.resolve() });
    var out = gate.tail.then(fn, fn);
    gate.tail = out.then(function(){}, function(){});
    return out;
}
function abortError(){
    try { return new DOMException('Aborted', 'AbortError'); }
    catch (_) { var e = new Error('Aborted'); e.name = 'AbortError'; return e; }
}
// First chunk includes webgpu shader compilation / wasm JIT warm-up —
// measured multi-second, give it 120 s. Steady-state chunks are seconds;
// 45 s only trips on a genuinely wedged backend.
var CHUNK_TIMEOUT_FIRST_MS = 120000;
// Steady-state timeout ADAPTS to the device: 6× the median observed chunk
// time, floored at 60 s. Rationale: with proxy=true (and on webgpu) chunks
// are genuinely async-pending, so a fixed 45 s could false-trip a slow
// phone whose honest chunks take 25-30 s — while a wedged backend shows a
// 6× departure from ITS OWN measured rate within a minute. Same adaptive
// philosophy as __withStallGuard's EMA (app.blade.php), sized per-chunk.
var CHUNK_TIMEOUT_FLOOR_MS = 60000;
// On WASM nothing wedges: the CPU always finishes the chunk, it is only slower
// on a hot or busy phone. Half of the mid-song timeouts in 60 d to 24.09.2026
// (21 of ~39 sessions) were wasm sessions — honest slow chunks thrown away
// with the whole separation. The bound there only has to catch a dead proxy
// worker, so it is wide: 20× the device's own median, floored at 5 min.
var CHUNK_TIMEOUT_WASM_FLOOR_MS = 300000;
function adaptiveChunkTimeout(durations, provider){
    var wasm = provider && provider !== 'webgpu';
    var floor = wasm ? CHUNK_TIMEOUT_WASM_FLOOR_MS : CHUNK_TIMEOUT_FLOOR_MS;
    if (!durations.length) return floor;
    var sorted = durations.slice().sort(function(a, b){ return a - b; });
    var median = sorted[Math.floor(sorted.length / 2)];
    return Math.max(floor, Math.round(median * (wasm ? 20 : 6)));
}

// ── split(): the same separation, streamed ──────────────────────────────────
//
// What it computes is IDENTICAL, bit for bit, to separating the whole song at
// once (same frames, same chunks, same mask blending, same float32 rounding at
// every step, same overlap-add order): the output was compared sample by sample
// against the whole-song version on a real song before this replaced it.
// What changes is what is held at the same time.
//
// The whole-song version kept, for the full length of the song: the STFT of
// both channels (magnitude + two phase arrays per frame), both masks, four
// masked magnitude spectrograms, four ISTFT outputs AND the two output
// buffers. For 294 s at 48 kHz that is ≈1.3 GB of JS arrays on the main thread
// on top of the decoded song — the size of the file #7527 (iPhone, 22.09.2026,
// «Страница вылетает») was separating. iOS kills a tab long before that.
//
// A frame's mask is final as soon as the last chunk that covers it has run
// (chunk c covers frames [c·STEP, c·STEP+CHUNK), so after chunk c every frame
// below (c+1)·STEP is final); a sample of the output is final once the last
// frame that overlaps it has been added. So the STFT is computed a chunk ahead,
// the masks live for one chunk, and each final frame is inverted straight into
// the output buffers and dropped. What stays is the input, the two output
// buffers and about a chunk of spectra (≈11 MB).
// One retry, and only for the case that has a better lane: WebGPU hung, the
// runtime was discarded above, the second pass runs on wasm.
async function split(audioBuffer, opts){
    try {
        return await splitOnce(audioBuffer, opts);
    } catch (e) {
        if (!(e && e.retryOnWasm) || (opts && opts.signal && opts.signal.aborted)) throw e;
        reportTelemetry((opts && opts.slug) || 'stem-separator', 'stem_webgpu_hang_wasm_retry', {});
        return await splitOnce(audioBuffer, opts);
    }
}

async function splitOnce(audioBuffer, opts){
    opts = opts || {};
    var slug = opts.slug || 'stem-separator';
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;
    var pct = function(p, stage){ if (onProgress) onProgress(p, stage); };

    var t0 = performance.now();

    // A caller that has moved on (a new file picked mid-separation) aborts:
    // the split stops at the next chunk boundary instead of running for
    // minutes into a page that no longer wants it.
    var signal = opts.signal || null;
    var checkAbort = function(){ if (signal && signal.aborted) throw abortError(); };

    pct(0.0, 'loading-model');
    await ensureModelLoaded(slug, function(p){ pct(0.05 + p * 0.20, 'loading-model'); });
    checkAbort();

    var ch = audioBuffer.numberOfChannels;
    var len = audioBuffer.length;
    var sr = audioBuffer.sampleRate;
    var L = audioBuffer.getChannelData(0);
    var R = ch > 1 ? audioBuffer.getChannelData(1) : L;
    var isMono = ch < 2;

    var NFFT = 2048, HOP = 1024, HALF = NFFT >> 1, FB = HALF + 1;
    var MB = 1024;                                   // bins the model sees
    var CHUNK = 256, OVERLAP = 32, STEP = CHUNK - 2 * OVERLAP;
    var nf = Math.floor((len - NFFT) / HOP) + 1;
    var numChunks = Math.ceil(nf / STEP);
    var inputName = onnxSession.inputNames[0];
    var outputName = onnxSession.outputNames[0];

    var win = new Float32Array(NFFT);
    for (var wi = 0; wi < NFFT; wi++) win[wi] = 0.5 * (1 - Math.cos(2 * Math.PI * wi / NFFT));

    // Outputs first: every final frame is inverted straight into them.
    var instrumental = emptyBuffer(2, len, sr);
    var vocal = emptyBuffer(2, len, sr);
    var outs = [instrumental.getChannelData(0), instrumental.getChannelData(1),
                vocal.getChannelData(0), vocal.getChannelData(1)];
    // Σ win² per sample, a ring over the two hops a frame spans. The same for
    // all four outputs (same window, same frames), so it is kept once.
    var wSum = new Float32Array(NFFT);

    // Per-frame arrays come from a pool and go back to it when the frame is
    // inverted: about a chunk and a half of frames are ever alive, so the pool
    // stops growing within the first chunks and the rest of the song allocates
    // nothing. Without it every frame left ~33 KB of garbage behind (≈450 MB
    // over a 5-minute song) and the peak depended on when the GC ran.
    var pool = [];
    function take(){ return pool.length ? pool.pop() : new Float32Array(FB); }
    function give(a){ pool.push(a); }

    // ---- STFT of one frame (what the whole-song aiSTFT did, one frame at a time) ----
    var fRe = new Float32Array(NFFT), fIm = new Float32Array(NFFT);
    function stftFrame(data, off){
        for (var i = 0; i < NFFT; i++) { fRe[i] = (off + i < len ? data[off + i] : 0) * win[i]; fIm[i] = 0; }
        fftInPlace(fRe, fIm);
        var m = take(), pr = take(), pi = take();   // every entry is written below
        for (var k = 0; k < FB; k++) {
            var mg = Math.sqrt(fRe[k] * fRe[k] + fIm[k] * fIm[k]);
            m[k] = mg;
            var inv = 1 / (mg + 1e-20);
            pr[k] = fRe[k] * inv;
            pi[k] = fIm[k] * inv;
        }
        return { m: m, pr: pr, pi: pi };
    }
    var frames = new Map();            // frame index → { l, r } spectra, until inverted
    var masks = new Map();             // frame index → { l, r } masks, until inverted
    function spectrum(f){
        var s = frames.get(f);
        if (!s) {
            var off = f * HOP;
            s = { l: stftFrame(L, off), r: stftFrame(R, off) };
            frames.set(f, s);
        }
        return s;
    }
    function maskOf(f){
        var m = masks.get(f);
        if (!m) { var a = take(), b = take(); a.fill(0); b.fill(0); m = { l: a, r: b }; masks.set(f, m); }
        return m;
    }
    function release(f){
        var sp = frames.get(f), mk = masks.get(f);
        if (sp) { give(sp.l.m); give(sp.l.pr); give(sp.l.pi); give(sp.r.m); give(sp.r.pr); give(sp.r.pi); frames.delete(f); }
        if (mk) { give(mk.l); give(mk.r); masks.delete(f); }
    }

    // ---- inversion of one final frame into one output ----
    var mag = new Float32Array(FB), iRe = new Float32Array(NFFT), iIm = new Float32Array(NFFT);
    function invert(f, magBuf, spec, out){
        var off = f * HOP;
        for (var k = 0; k <= HALF; k++) { iRe[k] = magBuf[k] * spec.pr[k]; iIm[k] = magBuf[k] * spec.pi[k]; }
        for (var k2 = 1; k2 < HALF; k2++) { iRe[NFFT - k2] = iRe[k2]; iIm[NFFT - k2] = -iIm[k2]; }
        ifftInPlace(iRe, iIm);
        for (var i = 0; i < NFFT; i++) {
            var idx = off + i;
            if (idx < len) out[idx] += iRe[i] * win[i];
        }
    }
    // mask-spread sample (the collapse detector), taken as frames are finalised
    var doSpread = numChunks >= 2 && nf >= 16 && MB >= 16;
    var stepNf = Math.max(1, Math.floor(nf / 16)), stepMb = Math.max(1, Math.floor(MB / 16));
    var spreadMin = Infinity, spreadMax = -Infinity;
    var nextFinal = 0;                 // first frame not yet inverted
    var normalized = 0;                // first sample not yet divided by Σ win²
    function normalizeUpTo(end){
        end = Math.min(end, len);
        for (var i = normalized; i < end; i++) {
            var j = i % NFFT, w = wSum[j];
            if (w > 1e-8) { outs[0][i] /= w; outs[1][i] /= w; outs[2][i] /= w; outs[3][i] /= w; }
            wSum[j] = 0;
        }
        if (end > normalized) normalized = end;
    }
    function finalizeUpTo(endFrame){
        for (var f = nextFinal; f < endFrame; f++) {
            var spec = spectrum(f), mk = maskOf(f);
            if (doSpread && f % stepNf === 0) {
                for (var sk = 0; sk < MB; sk += stepMb) {
                    var v = mk.l[sk];
                    if (v < spreadMin) spreadMin = v;
                    if (v > spreadMax) spreadMax = v;
                }
            }
            // instrumental = mask·|X| below the model's bins, |X| above them;
            // vocal = (1 − mask)·|X| below, nothing above.
            var k;
            for (k = 0; k < MB; k++) mag[k] = mk.l[k] * spec.l.m[k];
            for (k = MB; k < FB; k++) mag[k] = spec.l.m[k];
            invert(f, mag, spec.l, outs[0]);
            for (k = 0; k < MB; k++) mag[k] = mk.r[k] * spec.r.m[k];
            for (k = MB; k < FB; k++) mag[k] = spec.r.m[k];
            invert(f, mag, spec.r, outs[1]);
            for (k = 0; k < MB; k++) mag[k] = (1 - mk.l[k]) * spec.l.m[k];
            for (k = MB; k < FB; k++) mag[k] = 0;
            invert(f, mag, spec.l, outs[2]);
            for (k = 0; k < MB; k++) mag[k] = (1 - mk.r[k]) * spec.r.m[k];
            for (k = MB; k < FB; k++) mag[k] = 0;
            invert(f, mag, spec.r, outs[3]);
            var off = f * HOP;
            for (var i = 0; i < NFFT; i++) { if (off + i < len) wSum[(off + i) % NFFT] += win[i] * win[i]; }
            release(f);
            // no later frame reaches below (f+1)·HOP
            normalizeUpTo((f + 1) * HOP);
        }
        if (endFrame > nextFinal) nextFinal = endFrame;
    }

    var _chunkDurations = []; // observed per-chunk ms — feeds adaptiveChunkTimeout
    pct(0.30, 'inference');
    await yieldFrame();
    for (var c = 0; c < numChunks; c++) {
        checkAbort();
        var cs = c * STEP;
        // A NEW buffer per chunk, never a reused one: on the wasm path ORT runs
        // in its proxy worker and TRANSFERS the input tensor's buffer there —
        // the next chunk would find it detached ("Cannot perform
        // %TypedArray%.prototype.fill on a detached ArrayBuffer", measured in
        // Chromium when this loop reused it).
        var inp = new Float32Array(2 * MB * CHUNK);
        for (var f0 = 0; f0 < CHUNK; f0++) {
            var sf = cs + f0;
            if (sf < nf) {
                var sp = spectrum(sf);
                for (var k0 = 0; k0 < MB; k0++) {
                    inp[k0 * CHUNK + f0] = sp.l.m[k0];
                    inp[MB * CHUNK + k0 * CHUNK + f0] = sp.r.m[k0];
                }
            }
        }
        var tensor = new ort.Tensor('float32', inp, [1, 2, MB, CHUNK]);
        var feeds = {}; feeds[inputName] = tensor;
        var res;
        var _chunkT0 = performance.now();
        try {
            var _sess = onnxSession, _tmo = c === 0 ? CHUNK_TIMEOUT_FIRST_MS : adaptiveChunkTimeout(_chunkDurations, sessionProvider), _c = c;
            res = await ortRunSerialized(function(){
                _chunkT0 = visibleNow();
                return runWithTimeout(_sess.run(feeds), _tmo, _c);
            });
            _chunkDurations.push(visibleNow() - _chunkT0);
        } catch (runErr) {
            if (runErr && runErr.code === 'STEM_INFER_TIMEOUT') {
                // Wedged backend (webgpu device loss class). The hung run still
                // holds ORT's one-run-per-runtime guard, so the runtime itself is
                // unusable: discard it (a fresh script instance), and if the hang
                // was on WebGPU let split() go again on wasm — slower, but it
                // finishes instead of ending in an error.
                var onGpu = sessionProvider === 'webgpu';
                reportTelemetry(slug, 'stem_infer_timeout', { chunk: c, of: numChunks, provider: sessionProvider || '' });
                try { onnxSession.release(); } catch(_){}
                onnxSession = null; modelLoadPromise = null; sessionProvider = null;
                ortTrapReset(slug, 'cascaded_hang');
                if (onGpu) { webgpuWedged = true; runErr.retryOnWasm = true; }
                throw runErr;
            }
            if (isWasmTrap(runErr)) {
                ortTrapReset(slug, 'cascaded_run');
                try { onnxSession.release(); } catch(_){}
                onnxSession = null; modelLoadPromise = null;
                var trapErr = new Error('stem-separator wasm runtime trapped (memory access out of bounds)');
                trapErr.code = 'ORT_WASM_TRAP'; trapErr.expected = true; trapErr.cause = runErr;
                throw trapErr;
            }
            throw runErr;
        }
        var out = res[outputName].data;
        // Channel stride comes FROM THE TENSOR: CascadedNet takes [1,2,1024,256]
        // and returns [1,2,1025,256]. Reading it with stride 1024 shifted the
        // whole right mask by a bin (1.14 dB of vocal suppression on MUSDB18).
        var outDims = res[outputName].dims;
        var outBins = (outDims && outDims.length === 4) ? outDims[2] : MB;

        for (var f1 = 0; f1 < CHUNK; f1++) {
            var df = cs + f1;
            if (df >= nf) break;
            var w = 1.0;
            if (c > 0 && f1 < OVERLAP) w = f1 / OVERLAP;
            if (f1 >= CHUNK - OVERLAP && c < numChunks - 1) w = (CHUNK - f1) / OVERLAP;
            var mk1 = maskOf(df);
            for (var k1 = 0; k1 < MB; k1++) {
                var ml = out[k1 * CHUNK + f1];
                var mr = out[outBins * CHUNK + k1 * CHUNK + f1];
                if (w < 1.0) {
                    mk1.l[k1] = mk1.l[k1] * (1 - w) + ml * w;
                    mk1.r[k1] = mk1.r[k1] * (1 - w) + mr * w;
                } else {
                    mk1.l[k1] = ml;
                    mk1.r[k1] = mr;
                }
            }
        }
        // Chunk c+1 starts at (c+1)·STEP: every frame below it is final now.
        finalizeUpTo(c < numChunks - 1 ? (c + 1) * STEP : nf);
        pct(0.30 + 0.65 * ((c + 1) / numChunks), 'inference');
        await yieldFrame();
    }
    pct(0.97, 'reconstructing');
    finalizeUpTo(nf);
    normalizeUpTo(len);

    if (doSpread) {
        var maskSpread = spreadMax - spreadMin;
        if (maskSpread < 0.001) {
            // Same signature as the SONICS fp16 collapse — recorded, not thrown.
            reportTelemetry(slug, 'stem_mask_collapsed', {
                spread: Math.round(maskSpread * 100000) / 100000,
                mean: Math.round((spreadMin + spreadMax) / 2 * 1000) / 1000,
                chunks: numChunks
            });
        }
    }

    pct(1.0, 'done');
    reportTelemetry(slug, 'stem_split_ok', { ms: Math.round(performance.now() - t0), durationSec: audioBuffer.duration, channels: ch, modelMB: Math.round(modelLoadedSize / 1048576), lane: 'stream' });

    return { vocal: vocal, instrumental: instrumental, mono: isMono };
}

function isWarm(){ return !!onnxSession; }


function preload(slug, onProgressOrOpts){
    var s = slug || 'stem-separator';
    var opts = (typeof onProgressOrOpts === 'function')
        ? { onProgress: onProgressOrOpts }
        : (onProgressOrOpts || {});
    return ensureModelLoaded(s, opts.onProgress, opts.signal)
        .then(function(){ return true; })
        .catch(function(err){
            /* The caller's own Cancel is not «the engine failed to warm up»: it
               propagates as the AbortError it is (callers without a signal keep
               getting false — they cannot be cancelled). */
            if (opts.signal && opts.signal.aborted) throw err;
            // Don't swallow the error silently — emit detailed telemetry so
            // engine_warm_failed downstream can attribute the failure to a
            // real cause (CDN fetch failure, WebAssembly init crash, ORT
            // backend unavailable, OOM, etc.). Without this every UVR
            // preload failure shows up as engine_warm_failed{reason:""} —
            // 381 such events on /vocal-remover over the last 30 days were
            // all unattributable, hiding the actual root causes.
            try {
                reportTelemetry(s, 'stem_preload_failed', {
                    err_name: (err && err.name) || '',
                    reason: String((err && err.message) || err).slice(0, 200)
                });
            } catch(_){}
            return false;
        });
}

window.StemSeparator = {
    split: split,
    preload: preload,
    isWarm: isWarm,
    version: '1.0.0',
    engine: 'vocal-remover-fp16 (UVR-MDX-Net family)'
};

})();
